import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { 
  Search, 
  MapPin, 
  ArrowLeft,
  Loader2,
  Filter
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'land_clearing', label: 'Land Clearing' },
  { value: 'mulching', label: 'Mulching' },
  { value: 'excavation', label: 'Excavation' },
  { value: 'grading', label: 'Grading' },
  { value: 'brush_removal', label: 'Brush Removal' },
  { value: 'tree_removal', label: 'Tree Removal' },
  { value: 'site_prep', label: 'Site Preparation' }
];

export const JobsListPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLocation, setSearchLocation] = useState('');
  const [category, setCategory] = useState('all');

  useEffect(() => {
    fetchJobs();
  }, [category]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: 'open' });
      if (category && category !== 'all') {
        params.append('category', category);
      }
      if (searchLocation) {
        params.append('location', searchLocation);
      }
      const response = await axios.get(`${API}/jobs?${params}`);
      setJobs(response.data);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchJobs();
  };

  const getDashboardLink = () => {
    if (!user) return '/auth';
    return user.role === 'contractor' ? '/contractor/dashboard' : '/customer/dashboard';
  };

  return (
    <div className="min-h-screen bg-stone-950" data-testid="jobs-list-page">
      {/* Header */}
      <header className="bg-stone-900 border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_clearing-crew/artifacts/qizuc7gv_file_00000000d05471f59cc81c7b9868657c.png" 
                alt="IronLink" 
                className="h-14"
              />
            </Link>
            <div className="flex items-center gap-4">
              {user ? (
                <Link to={getDashboardLink()}>
                  <Button className="bg-orange-500 hover:bg-orange-600 text-white font-heading uppercase text-sm rounded-sm">
                    Dashboard
                  </Button>
                </Link>
              ) : (
                <Link to="/auth">
                  <Button className="bg-orange-500 hover:bg-orange-600 text-white font-heading uppercase text-sm rounded-sm">
                    Sign In
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Link 
          to={getDashboardLink()} 
          className="inline-flex items-center gap-2 text-stone-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Back</span>
        </Link>

        <h1 className="font-heading text-4xl text-white uppercase mb-8">Available Jobs</h1>

        {/* Filters */}
        <form onSubmit={handleSearch} className="industrial-card p-4 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" size={20} />
                <Input
                  placeholder="Search by location..."
                  value={searchLocation}
                  onChange={(e) => setSearchLocation(e.target.value)}
                  className="pl-10 bg-stone-900 border-stone-700 h-12"
                  data-testid="search-location"
                />
              </div>
            </div>
            <div className="w-full md:w-48">
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="bg-stone-900 border-stone-700 h-12" data-testid="category-filter">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent className="bg-stone-900 border-stone-700">
                  <SelectItem value="all">All Categories</SelectItem>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button 
              type="submit"
              data-testid="search-btn"
              className="bg-orange-500 hover:bg-orange-600 text-white font-heading uppercase h-12 px-8 rounded-sm"
            >
              <Search className="mr-2" size={20} />
              Search
            </Button>
          </div>
        </form>

        {/* Jobs Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
          </div>
        ) : jobs.length === 0 ? (
          <div className="industrial-card p-12 text-center">
            <Filter className="w-16 h-16 text-stone-600 mx-auto mb-4" />
            <h3 className="font-heading text-xl text-white uppercase">No Jobs Found</h3>
            <p className="text-stone-400 mt-2">Try adjusting your search filters</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {jobs.map((job) => (
              <Link 
                key={job.id} 
                to={`/jobs/${job.id}`}
                className="industrial-card p-6 hover:border-orange-500/50 transition-colors"
                data-testid={`job-listing-${job.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <Badge className="bg-orange-900/50 text-orange-400 border-orange-800 rounded-sm uppercase text-xs mb-2">
                      {job.category.replace('_', ' ')}
                    </Badge>
                    <h3 className="font-heading text-xl text-white uppercase">{job.title}</h3>
                    <p className="text-stone-400 mt-2 line-clamp-2">{job.description}</p>
                  </div>
                </div>
                
                <div className="flex flex-wrap items-center gap-4 mt-4 text-stone-400 text-sm">
                  <span className="flex items-center gap-1">
                    <MapPin size={14} />
                    {job.location}
                  </span>
                  <span>{job.acreage} acres</span>
                  <span className="text-orange-500">{job.bid_count} bids</span>
                </div>

                {job.budget_max && (
                  <div className="mt-4 pt-4 border-t border-stone-700">
                    <span className="font-heading text-2xl text-orange-500">
                      ${job.budget_min?.toLocaleString()} - ${job.budget_max?.toLocaleString()}
                    </span>
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};
